import { toJpeg } from 'html-to-image'
import { store } from '../store'
import { FormSchema, TemplateSchema } from './types/form'
import { SubmissionSchema } from './types/submission'

export const isMobile = () => {
  const windowWidth = window.screen.width

  // 80ch === 640px, 4rem === 64px
  const landscapeWidth = 704

  return windowWidth < landscapeWidth
}

export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const formatNum = (num: number) => {
  const isFloat = !!num.toString().split('.')[1]

  if (isFloat) {
    num = parseFloat(num.toFixed(2))
  }

  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const isFormDirty = (): boolean => {
  const formState = store.getState().form
  const { form } = formState.payload

  const check1 = form.name !== 'Untitled form'
  const check2 = form.description !== 'Add description...'
  const check3 = !!form.sections.length
  const check4 = form.meta.colour !== 'default'

  return check1 || check2 || check3 || check4
}

export const getDirtySubmissionForm = (): FormSchema | null => {
  const submissionState = store.getState().submission
  const { submission, meta } = submissionState.payload

  let isSubmissionDirty = false

  if (submission) {
    sectionLoop: for (const section of submission.sections) {
      for (const question of section.questions) {
        if (question.answer) {
          if (Array.isArray(question.answer)) {
            isSubmissionDirty = !!question.answer.length
          } else {
            isSubmissionDirty = true
          }

          if (isSubmissionDirty) {
            break sectionLoop
          }
        }
      }
    }

    if (isSubmissionDirty) {
      return meta?.form!
    }
  }

  return null
}

export const searchAndFilterForms = ({
  items,
  searchBy,
  filterBy
}: {
  items: (FormSchema | TemplateSchema)[]
  searchBy: string
  filterBy: string
}) => {
  return items.filter(({ name: formName, description: formDescription, sections, isPublic }) => {
    const isMatch =
      strContains(formName, searchBy) ||
      (formDescription && strContains(formDescription, searchBy)) ||
      sections.find(
        ({ name: sectionName, description: sectionDescription, questions }) =>
          (sectionName && strContains(sectionName, searchBy)) ||
          (sectionDescription && strContains(sectionDescription, searchBy)) ||
          questions.find(
            ({ statement, description: questionDescription }) =>
              strContains(statement, searchBy) ||
              (questionDescription && strContains(questionDescription, searchBy))
          )
      )

    return isMatch && (filterBy === 'public' ? isPublic : filterBy === 'private' ? !isPublic : true)
  })
}

const strContains = (field: string, value: string) =>
  field.toLowerCase().includes(value.toLowerCase())

export const manageEntryTotals = ({
  entriesPerDate,
  contextPerDate,
  submission,
  date,
  answer,
  isCumulative
}: {
  entriesPerDate: Map<number, number>
  contextPerDate: Map<number, SubmissionSchema[]>
  submission: SubmissionSchema
  date: number
  answer: number
  isCumulative?: boolean
}) => {
  const baseDate = setToBaseDate(date)
  const entryTotals = entriesPerDate.get(baseDate)
  const entryContext = contextPerDate.get(baseDate)

  entriesPerDate.set(
    baseDate,
    entryTotals ? (isCumulative ? answer : entryTotals + answer) : answer
  )

  contextPerDate.set(baseDate, entryContext ? [...entryContext, submission] : [submission])

  return { entriesPerDate, contextPerDate }
}

export const manageEntryTotalsNoDate = ({
  contextPerDate,
  submission,
  answer
}: {
  contextPerDate: Map<number, SubmissionSchema[]>
  submission: SubmissionSchema
  answer: number
}) => {
  const entryContext = contextPerDate.get(answer)

  contextPerDate.set(answer, entryContext ? [...entryContext, submission] : [submission])

  return { contextPerDate }
}

export const manageCatTotals = ({
  entriesPerCategory,
  contextPerCategory,
  submission,
  answer,
  value
}: {
  entriesPerCategory: Map<string, number>
  contextPerCategory: Map<string, SubmissionSchema[]>
  submission: SubmissionSchema
  answer: string
  value?: number
}) => {
  const catTotals = entriesPerCategory.get(answer)
  const entryContext = contextPerCategory.get(answer)
  const addValue = value ?? 1

  entriesPerCategory.set(answer, catTotals ? catTotals + addValue : addValue)

  contextPerCategory.set(answer, entryContext ? [...entryContext, submission] : [submission])

  return { entriesPerCategory, contextPerCategory }
}

export const manageCatTotalsColour = ({
  entriesPerCategory,
  contextPerCategory,
  submission,
  answer,
  value
}: {
  entriesPerCategory: Map<string, { name: string; value: number }>
  contextPerCategory: Map<string, SubmissionSchema[]>
  submission: SubmissionSchema
  answer: string
  value?: number
}) => {
  const catTotals = entriesPerCategory.get(answer)
  const entryContext = contextPerCategory.get(answer)
  const addValue = value ?? 1

  if (catTotals) {
    entriesPerCategory.set(answer, {
      ...catTotals,
      value: catTotals ? catTotals.value + addValue : addValue
    })
  }

  contextPerCategory.set(answer, entryContext ? [...entryContext, submission] : [submission])

  return { entriesPerCategory, contextPerCategory }
}

export const manageSet = (set: Set<string>, value: string, max?: boolean) => {
  set.has(value) ? set.delete(value) : max ? (() => {})() : set.add(value)

  return set
}

const normalizeNumber = (num: number): string => {
  return num < 10 ? `0${num}` : `${num}`
}

export const parseDate = (date: number | string, option: string): string => {
  if (!date) return ''

  const dateObj = new Date(date)
  let dateStr = ''

  switch (option) {
    case 'day':
      dateStr = new Intl.DateTimeFormat('en-US', {
        day: '2-digit'
      }).format(dateObj)
      break
    case 'date':
      dateStr = new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium'
      }).format(dateObj)
      break
    case 'min_date':
      dateStr = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short'
      }).format(dateObj)
      break
    case 'short_date':
      dateStr = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(dateObj)
      break
    case 'medium_date':
      dateStr = new Intl.DateTimeFormat('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(dateObj)
      break
    case 'long_date':
      dateStr = new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: '2-digit',
        year: 'numeric'
      }).format(dateObj)
      break
    case 'long_date_time':
      dateStr = new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      }).format(dateObj)
      break
    case 'time':
      dateStr = new Intl.DateTimeFormat('en-US', {
        timeStyle: 'short'
      }).format(dateObj)
      break
    default:
      const date = normalizeNumber(dateObj.getDate())
      const normalizedMonth = normalizeNumber(dateObj.getMonth() + 1)
      const year = dateObj.getFullYear()
      dateStr = `${date}-${normalizedMonth}-${year}`
  }

  return dateStr
}

export const setToCalendarDate = (date: number) => {
  // Format to yyyy-mm-dd
  const dateObj = new Date(date)

  const year = dateObj.getFullYear()
  let month: string | number = dateObj.getMonth() + 1
  let day: string | number = dateObj.getDate()

  if (month < 10) {
    month = `0${month}`
  }

  if (day < 10) {
    day = `0${day}`
  }

  return `${year}-${month}-${day}`
}

export const setToBaseDay = (value: number) => {
  const date = new Date(value)
  date.setHours(0)
  return date.getTime()
}

export const setToBaseDate = (date: number) => {
  const dateObj = new Date(date)

  dateObj.setHours(0)
  dateObj.setMinutes(0)
  dateObj.setSeconds(0)
  dateObj.setMilliseconds(0)

  return dateObj.getTime()
}

export const sortByNumber = (a: number, b: number) => {
  if (a < b) {
    return 1
  } else if (a > b) {
    return -1
  }

  return 0
}

export const sortByDateQuestion = (
  items: SubmissionSchema[],
  dateQuestionId: string,
  order: 'asc' | 'desc'
) => {
  return items.sort((q1, q2) => {
    let q1DateAnswer, q2DateAnswer

    q1.sections.forEach(({ questions }, sectionIndex) => {
      const dateQuestionIndex = questions.findIndex(({ id }) => id === dateQuestionId)
      if (dateQuestionIndex > -1) {
        q1DateAnswer = q1.sections[sectionIndex].questions[dateQuestionIndex].answer as number
        q2DateAnswer = q2.sections[sectionIndex].questions[dateQuestionIndex].answer as number
      }
    })

    if (q1DateAnswer && q2DateAnswer) {
      if (q1DateAnswer > q2DateAnswer) {
        return order === 'asc' ? 1 : -1
      } else if (q1DateAnswer < q2DateAnswer) {
        return order === 'asc' ? -1 : 1
      }
    }

    return 0
  })
}

export const sortByDateSubmitted = (items: SubmissionSchema[]) => {
  return items.sort((q1, q2) => {
    if (q1.submittedOn > q2.submittedOn) {
      return 1
    } else if (q1.submittedOn < q2.submittedOn) {
      return -1
    }

    return 0
  })
}

export const getFormThumbnailUrl = async (
  el: HTMLElement,
  exclusionClasses: string[],
  height?: number
) => {
  try {
    const options: {
      quality: number
      backgroundColor: string | undefined
      filter: (node: HTMLElement) => boolean
      height?: number
    } = {
      quality: 0.5,
      backgroundColor: '#FFF',
      filter: (node) => !exclusionClasses.some((classname) => node.classList?.contains(classname))
    }

    if (height) {
      options.height = height
    }

    const dataUrl = await toJpeg(el, options)

    return dataUrl
  } catch (error) {
    return Promise.reject(error)
  }
}

export const captureScreenshot = async (
  e: KeyboardEvent,
  ref: React.RefObject<HTMLFormElement | null>,
  skipClasses: string[],
  height?: number
) => {
  if (e.key === '~' && ref.current) {
    const thumbnailUrl = await getFormThumbnailUrl(ref.current, skipClasses, height)

    if (thumbnailUrl) {
      const link = document.createElement('a')
      link.download = 'thumbnail.jpeg'
      link.href = thumbnailUrl
      link.click()
    }
  }
}

export const toggleExpandContextMenu = (
  ref: HTMLDivElement,
  isExpanded: Record<string, boolean>,
  setIsExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  wrapped: boolean = false
) => {
  const isElementMidSize = ref.classList.contains(`context_menu-${wrapped ? 'wrap' : 'md'}`)

  if (isElementMidSize) {
    ref.classList.remove(`context_menu-${wrapped ? 'wrap' : 'md'}`)
    ref.classList.add('context_menu-lg')
    setIsExpanded({ ...isExpanded, [ref.id]: true })
  } else {
    ref.classList.remove('context_menu-lg')
    ref.classList.add(`context_menu-${wrapped ? 'wrap' : 'md'}`)
    setIsExpanded({ ...isExpanded, [ref.id]: false })
  }
}
